/* eslint-disable @typescript-eslint/no-explicit-any */
import { centrePath, cumulativeLengths, DIMMED_OPACITY, Dot, dotAt, dotsFor, DOT_RADIUS, FlowRibbon, layoutFlow, ribbonPaint } from "./flowRibbonMath";
import { CentreSample } from "./funnelRibbons";

/*
 * Draws the FLOW LINES as RIBBONS (the maths and the reasons are in flowRibbonMath.ts). The ribbons sit BEHIND the page boxes, in a layer of their own right after the grey background (the
 * same place as the funnel's), with the flowing dots in the same layer; the invisible hover targets stay with the dashed lines' (clusterService), above the boxes, so a ribbon can be
 * hovered even where a box covers it.
 *
 * The dots are moved by ONE requestAnimationFrame loop (never one timer per dot), which rewrites ONE attribute (the `d` of the single path that draws every dot) at most 60 times a
 * second, stops by itself when the layer is gone or the tab is hidden (a hidden tab does not run frames, and nothing is queued to replay when it comes back: the position is worked out
 * from the clock, not accumulated), and does not run at all for a visitor who asked their system for reduced motion (the dots are then drawn once, at rest).
 *
 * (2026-10-18: each dot used to be its own <circle> with its cx and cy rewritten every frame. With every ribbon shown that was ~300 dots, and on a 120 Hz screen ~70,000 style and layout
 * invalidations a second - measured in a trace of the real page, the largest single source of them - for a picture that is one set of small circles. One path draws the same circles.)
 */
export { layoutFlow, centrePath };
export { FLOW_RIBBON_COUNT_OPTIONS, FLOW_RIBBON_TOP_N } from "./flowRibbonMath";

export const FLOW_RIBBON_LAYER = "flow-ribbons-layer";
const DOT_FILL = "#ffffff";
const DOT_STROKE = "rgba(40, 80, 200, 0.85)";

interface DotRef {
  ribbon: FlowRibbon;
  centre: CentreSample[];
  /** the distance along the centre line at each sample, so the dot is placed by distance (one pace for all) */
  cum: number[];
  length: number;
  dot: Dot;
  /** where it was last put (for the real-browser checks, which judge the dots by where they are) */
  x: number;
  y: number;
}

// (a holder object, not a module-level `let dots: DotRef[]` that is reassigned: the build's Babel Flow-strip step failed on exactly that pair, "UnionTypeAnnotation ... TSArrayType")
const live: { dots: DotRef[] } = { dots: [] };
let raf = 0;

const reducedMotion = (): boolean => {
  try {
    return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch (e) {
    return false;
  }
};

/** one small circle as relative path commands, starting from its centre: out to the left edge, round the top and back, round the bottom and back */
const CIRCLE = `m${-DOT_RADIUS} 0a${DOT_RADIUS} ${DOT_RADIUS} 0 1 0 ${2 * DOT_RADIUS} 0a${DOT_RADIUS} ${DOT_RADIUS} 0 1 0 ${-2 * DOT_RADIUS} 0`;
/** the dots are redrawn at most this often (60 a second): a 120 Hz screen gets every second frame, which is all the picture needs and half the work */
const MIN_REDRAW_GAP_MS = 12.5;
let lastRedrawAt = -1e9;

const place = (timeMs: number) => {
  const el = document.querySelector(`#${FLOW_RIBBON_LAYER} path.flow-dots`);
  const parts: string[] = [];
  for (const d of live.dots) {
    const p = dotAt(d.centre, d.length, d.dot, timeMs, d.cum);
    d.x = p.x;
    d.y = p.y;
    parts.push(`M${p.x.toFixed(1)} ${p.y.toFixed(1)}${CIRCLE}`);
  }
  if (el) el.setAttribute("d", parts.join(""));
};

if (typeof window !== "undefined") {
  // for the real-browser checks (scripts/flow-ribbons-check.mjs): every dot, with its ribbon and where it is now
  (window as any).__flowDots = () =>
    live.dots.map((d) => ({
      key: d.ribbon.key,
      weight: d.ribbon.weight,
      width: d.ribbon.width,
      length: d.ribbon.length,
      centre: d.ribbon.shape.centre,
      lane: d.dot.lane,
      phase: d.dot.phase,
      x: d.x,
      y: d.y,
    }));
}

const stopLoop = () => {
  if (raf) cancelAnimationFrame(raf);
  raf = 0;
};

const tick = () => {
  raf = 0;
  // the layer is gone (flow lines switched off, the zoomed view, a reset): stop, and let go of the dots
  if (!live.dots.length || !document.querySelector(`#${FLOW_RIBBON_LAYER} path.flow-dots`)) {
    live.dots = [];

    return;
  }
  const now = performance.now();
  if (!document.hidden && now - lastRedrawAt >= MIN_REDRAW_GAP_MS) {
    lastRedrawAt = now;
    place(now);
  }
  raf = requestAnimationFrame(tick);
};

const startLoop = () => {
  stopLoop();
  if (!live.dots.length) return;
  if (reducedMotion()) {
    place(0);

    return;
  }
  place(performance.now());
  raf = requestAnimationFrame(tick);
};

const ensureLayer = (svg: any) => {
  let layer = svg.select(`#${FLOW_RIBBON_LAYER}`);
  if (layer.empty()) {
    // Right after the grey background, so it sits UNDER every box group (the ribbons run behind the boxes and the boxes join up).
    const back = svg.select("#maincluster").node();
    layer = svg.insert("g", () => (back ? back.nextSibling : null)).attr("id", FLOW_RIBBON_LAYER).style("pointer-events", "none");
  }

  return layer;
};

/** The busiest weight among the ribbons on the layer (what every ribbon's colour is measured against). */
const maxWeightOf = (ribbons: FlowRibbon[]): number => ribbons.reduce((m, r) => Math.max(m, r.weight, r.refWeight || 0), 0);

/**
 * Paints every ribbon from its traffic (colour, solidity and outline: flowRibbonMath.ribbonPaint), the hovered one strong and every other one dimmed while one is hovered, so the one the pointer is
 * on stands out of a crowd (Chris, 2026-10-19: ribbons covering each other). Called after every redraw as well as on hover and leave, so a hover is never undone by a redraw.
 */
const paintRibbons = (layer: any, max: number, hoveredKey: string | null) => {
  layer
    .selectAll("path.flow-ribbon")
    .attr("fill", (d: FlowRibbon) => ribbonPaint(d.weight, max, d.key === hoveredKey).fill)
    .attr("stroke", (d: FlowRibbon) => ribbonPaint(d.weight, max, d.key === hoveredKey).edge)
    .attr("stroke-width", (d: FlowRibbon) => ribbonPaint(d.weight, max, d.key === hoveredKey).edgeWidth)
    .attr("opacity", (d: FlowRibbon) => (hoveredKey && d.key !== hoveredKey ? DIMMED_OPACITY : null));
};

/**
 * The stacking order of the ribbons: the busiest at the back and the smallest in front (a thin ribbon is the one that gets buried, so it must be the one on top), and the hovered one in front of
 * all of them, just under the dots. d3's sort moves only the nodes that are out of place.
 */
const stackRibbons = (layer: any, hoveredKey: string | null) => {
  layer.selectAll("path.flow-ribbon").sort((a: FlowRibbon, b: FlowRibbon) => b.weight - a.weight || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
  const dots = layer.select("path.flow-dots").node();
  if (!hoveredKey || !dots) return;
  const hovered = layer.selectAll("path.flow-ribbon").filter((d: FlowRibbon) => d.key === hoveredKey).node();
  if (hovered && hovered.nextSibling !== dots) dots.parentNode.insertBefore(hovered, dots);
};

/** Draws `ribbons` (and their dots), replacing what was drawn before. Dots that are still wanted keep their element, so a redraw does not make them jump. */
export const renderFlowRibbons = (svg: any, ribbons: FlowRibbon[], hoveredKey: string | null = null) => {
  const layer = ensureLayer(svg);
  const max = maxWeightOf(ribbons);

  // the one path that draws every dot, above the ribbons (it is created first so the ribbons can be inserted before it)
  if (layer.select("path.flow-dots").empty()) layer.append("path").attr("class", "flow-dots").attr("fill", DOT_FILL).attr("stroke", DOT_STROKE).attr("stroke-width", 0.9);

  const paths = layer.selectAll("path.flow-ribbon").data(ribbons, (d: FlowRibbon) => d.key);
  paths.exit().remove();
  paths
    .enter()
    .insert("path", "path.flow-dots")
    .attr("class", "flow-ribbon")
    .merge(paths)
    .attr("data-key", (d: FlowRibbon) => d.key)
    .attr("d", (d: FlowRibbon) => d.shape.d);
  // the hovered ribbon STAYS strong across redraws: the picture is redrawn every couple of seconds while traffic is counted, and a redraw used to put every ribbon back to its resting
  // colour, so a ribbon hovered for longer than that faded back under the pointer (Chris, 2026-10-18)
  paintRibbons(layer, max, hoveredKey);
  stackRibbons(layer, hoveredKey);

  // a dot keeps its identity (`key#i`) and its place along the ribbon (its phase) across redraws, so it does not jump when the ribbons are redrawn
  const before = new Map<string, { x: number; y: number }>(live.dots.map((d) => [`${d.dot.key}#${d.dot.i}`, { x: d.x, y: d.y }]));
  const refs: DotRef[] = [];
  ribbons.forEach((r) =>
    dotsFor(r.key, r.weight, max).forEach((dot) => {
      const was = before.get(`${dot.key}#${dot.i}`);
      refs.push({ ribbon: r, centre: r.shape.centre, cum: cumulativeLengths(r.shape.centre), length: r.length, dot, x: was ? was.x : 0, y: was ? was.y : 0 });
    })
  );
  live.dots = refs;
  lastRedrawAt = -1e9;
  if (!raf) startLoop();
  else place(performance.now());
};

/** Removes every ribbon and dot and stops the animation. Safe to call with no svg or no layer. */
export const clearFlowRibbons = (svg: any) => {
  stopLoop();
  live.dots = [];
  svg?.select(`#${FLOW_RIBBON_LAYER}`)?.selectAll("*")?.remove();
};

/** The hovered ribbon is drawn strong and in front, every other one dimmed (the hover target is a separate, invisible path above the boxes); `on` false puts them all back. */
export const highlightFlowRibbon = (svg: any, key: string, on: boolean) => {
  const layer = svg?.select(`#${FLOW_RIBBON_LAYER}`);
  if (!layer || layer.empty()) return;
  const ribbons: FlowRibbon[] = layer.selectAll("path.flow-ribbon").data();
  paintRibbons(layer, maxWeightOf(ribbons), on ? key : null);
  stackRibbons(layer, on ? key : null);
};
